/**
 * Relocated from `publicationViewEffects.ts` so it survives that file's
 * deletion. Pure async helper — no compose-app state.
 *
 * Resolves the story-content ids (or full contents) that embed a given set of
 * embeds, by querying embed→story mapping then the story contents and scanning
 * each content's `.media-block` script tags for matching `data-embed-id`s.
 *
 * Used by `useUpdateDataFrequency` and `useDeleteReportEmbeds`.
 */

import { getStoryContentsByIds } from '@opengov/stories-shared/graphQL';
import { IStoryContent } from '@opengov/stories-shared/types';

import { postQuery } from '../../../../effects/publications/postQuery';
import { getEmbedDataQuery } from '../../../../graphQL/queries';
import { TAG_SCRIPT } from '../../../../constants';

export const getStoryContentsByEmbedIds = async (
  variables: { embedId: string; storyId: string }[],
  fullContent = false
): Promise<string[] | IStoryContent[]> => {
  const embedDataQuery = getEmbedDataQuery(variables);
  const response = await postQuery(embedDataQuery, {});
  const embedIds: string[] = variables.map((val) => val.embedId);
  if (response.errors) throw Error(response.errors[0].message);
  const embedResponse = Object.keys(response)
    .map((w) => response[w])
    .flat();
  const storyEmbedsMapping = new Map<string, string[]>([]);
  const storyContentIds: string[] = [];
  embedResponse.forEach((item) => {
    if (storyEmbedsMapping.has(item.storyId)) {
      storyEmbedsMapping.set(item.storyId, [...storyEmbedsMapping.get(item.storyId), item.embedId]);
    } else {
      storyEmbedsMapping.set(item.storyId, [item.embedId]);
    }
  });
  const storyIds: string[] = [...new Set(embedResponse.map((item) => item.storyId))];
  if (storyIds.length === 0) return [];
  const storyContentsResponse = await postQuery(getStoryContentsByIds, { storyIds });
  if (storyContentsResponse.storyContents.length === 0) return [];
  if (fullContent) return storyContentsResponse.storyContents;
  storyContentsResponse.storyContents.forEach((storyContent) => {
    if (storyContent.group === 'header' || storyContent.group === 'footer') return;
    const fragment = document.createRange().createContextualFragment(storyContent.content);
    const mediaBlocks = fragment.querySelectorAll('.media-block');
    mediaBlocks.forEach((mediaBlock) => {
      for (let i = 0; i < mediaBlock.children.length; i++) {
        if (mediaBlock.children[i].tagName === TAG_SCRIPT && mediaBlock.children[i].hasAttribute('data-embed-id')) {
          const embedId = mediaBlock.children[i].getAttribute('data-embed-id');
          if (embedIds.includes(embedId)) {
            storyContentIds.push(storyContent._id);
          }
        }
      }
    });
  });
  return storyContentIds;
};
